import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/recovery_settings';

const REMINDER_THRESHOLD_TYPES = ['remaining_balance', 'overdue_balance'] as const;
const REMINDER_TIME_SLOTS = ['morning', 'afternoon'] as const;

export const RecoverySettingsSchema = z.object({
  automaticReminders: z.boolean(),
  daysBetweenReminder: z.number().int().min(0),
  thresholdAmount: z.number().min(0),
  minimumSaldoSending: z.number().min(0),
  reminderThresholdType: z.enum(REMINDER_THRESHOLD_TYPES),
  reminderTimeSlot: z.enum(REMINDER_TIME_SLOTS),
  defaultFormalNoticeTemplateId: z.string().nullable(),
  defaultAdditionalReminderTemplateId: z.string().nullable(),
  sendRemindersForImportedInvoices: z.boolean(),
});

export type RecoverySettings = z.infer<typeof RecoverySettingsSchema>;

export async function loadRecoverySettingsQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = RecoverySettingsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'recoverySettings');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadRecoverySettings(options: QueryOptions<RecoverySettings> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<RecoverySettings, ApiError>({
    queryKey: ['recoverySettings'],
    queryFn: () => loadRecoverySettingsQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<RecoverySettings, ApiError, 'recoverySettings'>(
    'recoverySettings',
    queryResult,
  );
}
